import { useEffect, useState } from 'react';
import { extractErrorMessage } from '../../api/errors';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui';
import { EmailSeries, SeriesDiscountOption, useUpdateEmailSeries } from '../../queries/emailSeries';
import { formatCalendarDate, formatSendHour } from './emailSeriesFormat';

// A series' offer and timing. Copy lives on its steps (EmailSeriesStepDialog).

interface SettingsForm {
  name: string;
  discountId: string;
  codeValidDays: string;
  sendHour: string;
  lookbackDays: string;
  reenrolAfterDays: string;
}

const toForm = (s: EmailSeries): SettingsForm => ({
  name: s.name,
  discountId: s.discountId === null ? '' : String(s.discountId),
  codeValidDays: s.codeValidDays === null ? '' : String(s.codeValidDays),
  sendHour: String(s.sendHour),
  lookbackDays: String(s.lookbackDays),
  reenrolAfterDays: String(s.reenrolAfterDays),
});

const INPUT = 'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm disabled:opacity-60';
const HOURS = Array.from({ length: 24 }, (_, h) => h);

function optionText(d: SeriesDiscountOption): string {
  const notes = [d.synced ? null : 'not synced to Stripe', d.endDate ? `ends ${formatCalendarDate(d.endDate)}` : null].filter(Boolean);
  return `${d.discountCode} - ${d.label ?? d.name}${notes.length ? ` (${notes.join(', ')})` : ''}`;
}

interface EmailSeriesSettingsProps {
  series: EmailSeries;
  /** From the series detail, so it needs only marketing access. */
  discountOptions: SeriesDiscountOption[];
  canWrite: boolean;
}

export function EmailSeriesSettings({ series, discountOptions, canWrite }: EmailSeriesSettingsProps) {
  const update = useUpdateEmailSeries();
  const [form, setForm] = useState<SettingsForm>(() => toForm(series));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Pick up the saved values after a save, or a change made elsewhere.
  useEffect(() => setForm(toForm(series)), [series]);

  const set = (key: keyof SettingsForm) => (e: { target: { value: string } }) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  // The saved discount may since have been switched off, so it drops out of the options.
  const currentMissing = series.discountId !== null && !discountOptions.some((d) => d.id === series.discountId);

  const save = () => {
    setError(null);
    setSaved(false);
    update.mutate(
      {
        id: series.id,
        name: form.name.trim(),
        discountId: form.discountId ? Number(form.discountId) : null,
        codeValidDays: form.codeValidDays.trim() ? Number(form.codeValidDays) : null,
        sendHour: Number(form.sendHour),
        lookbackDays: Number(form.lookbackDays),
        reenrolAfterDays: Number(form.reenrolAfterDays),
      },
      { onSuccess: () => setSaved(true), onError: (err) => setError(extractErrorMessage(err, 'Could not save the settings.')) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>Each customer gets their own single-use code on the chosen discount.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="series-name" className="mb-1 block text-sm font-medium">Name</label>
            <input id="series-name" value={form.name} onChange={set('name')} disabled={!canWrite} className={INPUT} />
          </div>
          <div>
            <label htmlFor="series-discount" className="mb-1 block text-sm font-medium">Offer</label>
            <select id="series-discount" value={form.discountId} onChange={set('discountId')} disabled={!canWrite} className={INPUT}>
              <option value="">No offer</option>
              {currentMissing && (
                <option value={String(series.discountId)}>{series.discountCode ?? `Discount ${series.discountId}`} (not active)</option>
              )}
              {discountOptions.map((d) => (
                <option key={d.id} value={String(d.id)}>{optionText(d)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="series-valid-days" className="mb-1 block text-sm font-medium">Code valid for (days)</label>
            <input id="series-valid-days" type="number" min={1} max={90} value={form.codeValidDays} onChange={set('codeValidDays')} disabled={!canWrite} className={INPUT} />
          </div>
          <div>
            <label htmlFor="series-send-hour" className="mb-1 block text-sm font-medium">Send at (Brisbane time)</label>
            <select id="series-send-hour" value={form.sendHour} onChange={set('sendHour')} disabled={!canWrite} className={INPUT}>
              {HOURS.map((h) => <option key={h} value={String(h)}>{formatSendHour(h)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="series-lookback" className="mb-1 block text-sm font-medium">Look back (days)</label>
            <input id="series-lookback" type="number" min={1} max={30} value={form.lookbackDays} onChange={set('lookbackDays')} disabled={!canWrite} className={INPUT} />
            <p className="mt-1 text-xs text-muted-foreground">How far back a trigger can be and still enrol someone.</p>
          </div>
          <div>
            <label htmlFor="series-reenrol" className="mb-1 block text-sm font-medium">Re-enrol after (days)</label>
            <input id="series-reenrol" type="number" min={0} max={3650} value={form.reenrolAfterDays} onChange={set('reenrolAfterDays')} disabled={!canWrite} className={INPUT} />
            <p className="mt-1 text-xs text-muted-foreground">How long before the same customer can go through this series again.</p>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {canWrite && (
          <div className="flex items-center justify-end gap-3">
            {saved && <span className="text-sm text-muted-foreground">Saved.</span>}
            <Button size="sm" onClick={save} disabled={update.isPending || !form.name.trim()}>Save settings</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
